import * as R from 'remeda'

import { type Brand, type Question } from '~/hooks/useSurveyQuery'
import {
  exists,
  mapNineSegment,
  type NineSegment,
  type NineSegmentCounts,
  nineSegments,
} from '~/utils'

import { type NineSegmentsData } from './nineSegments'

// Shape the existing-page hook flattens
// `tabulationQueryData?.question?.results` into. Engine basic-output passes the
// same shape after a structural reshape (no math). Nullable fields match the
// GraphQL-generated optional/null shape so the hook can pass through directly.
export type CountAnswer = {
  questionId: string
  groupAnswers?: ReadonlyArray<{
    group: string
    answers?: ReadonlyArray<{
      answerOption?: { option: number; text: string } | null
      count: number
    }> | null
  }> | null
}

export type NineSegmentsAnswerData = NineSegmentsData & {
  question: Question
  answerOption: { option: number; text: string }
}

export type NineSegmentsAnswersArrayDataInput = {
  brand: Brand
  countAnswers: ReadonlyArray<CountAnswer>
  // The curried per-row builder produced by useGetNineSegmentsData. Wraps
  // buildNineSegmentsData with a totalDataCounts closure resolved by the hook
  // (or by basic-output's adapter for the offline render path).
  getNineSegmentsData: (
    nineSegmentCounts: NineSegmentCounts,
    total: number,
  ) => NineSegmentsData
  getBrandNineSegmentIndex: (brand: Brand, nineSegment: NineSegment) => number
  questions: ReadonlyArray<Question>
  // Sort function from useAtomValue(nineSegmentSortFunctionAtom) on the page;
  // basic-output supplies identity for offline render. Mutable array param to
  // match the page's generic `<T extends NineSegmentsData>(array: T[]) => T[]`
  // signature.
  sort: (rows: NineSegmentsAnswerData[]) => NineSegmentsAnswerData[]
}

export const buildNineSegmentsAnswersArrayData = (
  input: NineSegmentsAnswersArrayDataInput,
): NineSegmentsAnswerData[][] =>
  input.countAnswers.map((countAnswer) => {
    const question = input.questions.find(
      ({ question_id }) => question_id === countAnswer.questionId,
    ) as Question
    const answerOptions =
      countAnswer.groupAnswers?.[0]?.answers
        ?.map(R.prop('answerOption'))
        .filter(exists) ?? []

    const nineSegmentAnswers = mapNineSegment((nineSegment) => {
      const index = input.getBrandNineSegmentIndex(input.brand, nineSegment)

      return countAnswer.groupAnswers?.[index]?.answers ?? []
    })

    return R.pipe(
      answerOptions,
      R.map((answerOption) => {
        const nineSegmentCounts = mapNineSegment(
          (nineSegment) =>
            nineSegmentAnswers[nineSegment].find(
              (answer) => answer.answerOption?.option === answerOption.option,
            )?.count ?? NaN,
        )
        const total = R.sumBy(
          nineSegments,
          (nineSegment) => nineSegmentCounts[nineSegment],
        )

        return {
          question,
          answerOption,
          ...input.getNineSegmentsData(nineSegmentCounts, total),
        }
      }),
      input.sort,
    )
  })
